/**
 * usePrizeOps — React Query hooks for the dynamic-prize feature.
 *
 * Endpoints under /api/prize-ops:
 *   GET    /org-defaults?organization_id=…   — list org default adjustments (auth)
 *   POST   /org-defaults                     — create (admin)
 *   PUT    /org-defaults/:id                 — update (admin)
 *   DELETE /org-defaults/:id                 — delete (admin)
 *   GET    /events/:eventId/prize            — live computed prize + breakdown (auth)
 *
 * The compute endpoint is the authoritative source for the contestant-facing
 * display; the org-default hooks back the organization settings section and the
 * event-setup adjustments list. The numeric engine is mirrored locally in
 * utils/prize-calculation.ts for instant in-form previews before save.
 */

import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { httpClient } from '../http-client';
import type {
  PrizeAllocationMode,
  PrizeCalculationResult,
} from '../../utils/prize-calculation';

export interface OrgDefaultPrizeAdjustment {
  id: string;
  organization_id: string;
  adjustment_type: 'addition' | 'subtraction';
  amount: number;
  percentage: number | null;
  reason: string;
  description: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface OrgDefaultPrizeAdjustmentInput {
  organization_id: string;
  adjustment_type: 'addition' | 'subtraction';
  amount?: number;
  percentage?: number | null;
  reason: string;
  description?: string | null;
  is_active?: boolean;
  sort_order?: number;
}

export interface EffectiveAdjustment {
  id: string;
  source: 'org_default' | 'custom';
  adjustment_type: 'addition' | 'subtraction';
  amount: number;
  percentage: number | null;
  reason: string;
  description: string | null;
  is_active: boolean;
  locked_wording: boolean;
  deletable: boolean;
}

export interface PrizeRoundPlace {
  place: number;
  amount: number;
}

export interface PrizeRoundBreakdown {
  round_number: number;
  round_name: string;
  is_average: boolean;
  /** Performance the round is tied to; null for the Average row or an untied round. */
  performance_id?: string | null;
  total: number;
  places: PrizeRoundPlace[];
}

export interface EventPrizeComputation {
  event_id: string;
  allocation_mode: PrizeAllocationMode;
  entry_count: number;
  display_to_contestants: boolean;
  effective_adjustments: EffectiveAdjustment[];
  calculation: PrizeCalculationResult;
  prize_rounds: PrizeRoundBreakdown[];
}

export interface OrgPrizeSettings {
  default_entry_fee_prize_portion: number;
  default_entry_fee_prize_portion_is_percentage: boolean;
  default_entry_fee_prize_portion_is_fee_deduction: boolean;
}

export const prizeOpsKeys = {
  all: ['prize-ops'] as const,
  orgDefaults: (orgId: string) => [...prizeOpsKeys.all, 'org-defaults', orgId] as const,
  orgPrizeSettings: (orgId: string) => [...prizeOpsKeys.all, 'org-prize-settings', orgId] as const,
  eventPrize: (eventId: string, entryCount?: number) =>
    [...prizeOpsKeys.all, 'event-prize', eventId, entryCount ?? 'live'] as const,
};

/** Org-level default per-entry prize contribution (pre-fills new events). */
export function useOrgPrizeSettings(organizationId: string | null | undefined) {
  return useQuery({
    queryKey: prizeOpsKeys.orgPrizeSettings(organizationId || ''),
    enabled: !!organizationId,
    queryFn: async () => {
      const res = await httpClient.get<{ data: OrgPrizeSettings; success: boolean }>(
        `/prize-ops/org-prize-settings?organization_id=${organizationId}`,
      );
      return res.data;
    },
    staleTime: 60 * 1000,
  });
}

export function useUpdateOrgPrizeSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      organizationId,
      settings,
    }: {
      organizationId: string;
      settings: Partial<OrgPrizeSettings>;
    }) => {
      const res = await httpClient.put<{ data: OrgPrizeSettings; success: boolean }>(
        `/prize-ops/org-prize-settings/${organizationId}`,
        settings,
      );
      return { organizationId, data: res.data };
    },
    onSuccess: ({ organizationId, data }) => {
      qc.setQueryData(prizeOpsKeys.orgPrizeSettings(organizationId), data);
    },
  });
}

/** List an organization's default prize adjustments. */
export function useOrgDefaultPrizeAdjustments(organizationId: string | null | undefined) {
  return useQuery({
    queryKey: prizeOpsKeys.orgDefaults(organizationId || ''),
    enabled: !!organizationId,
    queryFn: async () => {
      const res = await httpClient.get<{ data: OrgDefaultPrizeAdjustment[]; success: boolean }>(
        `/prize-ops/org-defaults?organization_id=${organizationId}`,
      );
      return res.data;
    },
    staleTime: 60 * 1000,
  });
}

export function useCreateOrgDefaultPrizeAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: OrgDefaultPrizeAdjustmentInput) => {
      const res = await httpClient.post<{ data: OrgDefaultPrizeAdjustment; success: boolean }>(
        '/prize-ops/org-defaults',
        input,
      );
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: prizeOpsKeys.orgDefaults(data.organization_id) });
    },
  });
}

export function useUpdateOrgDefaultPrizeAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: string;
      updates: Partial<OrgDefaultPrizeAdjustmentInput>;
    }) => {
      const res = await httpClient.put<{ data: OrgDefaultPrizeAdjustment; success: boolean }>(
        `/prize-ops/org-defaults/${id}`,
        updates,
      );
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: prizeOpsKeys.orgDefaults(data.organization_id) });
    },
  });
}

export function useDeleteOrgDefaultPrizeAdjustment(organizationId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await httpClient.delete(`/prize-ops/org-defaults/${id}`);
      return id;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: prizeOpsKeys.orgDefaults(organizationId) });
    },
  });
}

/** Minimal event shape needed to resolve a display prize. */
export interface PrizeEventLike {
  id: string;
  prize_allocation_mode?: string | null;
  display_prize_to_contestants?: boolean | null;
  total_prize_amount?: number | null;
}

/** The prize is shown to contestants (the universal gate for any display). */
function showsPrize(e: PrizeEventLike): boolean {
  return !!e.display_prize_to_contestants;
}

/**
 * Sum the display prize across a set of events, where each event uses its
 * AFTER-adjustment computed total (fixed and dynamic both run through the
 * compute endpoint, which applies the org-default + custom adjustments and,
 * for dynamic events, multiplies the per-entry contribution by the current
 * entry count). Batches the compute calls via useQueries; the static base is a
 * fallback while a request is in flight.
 *
 * By default only events whose prize is shown to contestants count (the
 * contestant-facing total). Pass `{ requireDisplay: false }` to sum EVERY
 * event regardless of the contestants toggle — used by the admin/delegate
 * "My Rodeos" header, which mirrors the per-event prize pools shown in the
 * expanded list (those rows are not gated on the contestants toggle).
 */
export function useEventsPrizeTotal(
  events: PrizeEventLike[],
  opts?: { requireDisplay?: boolean },
): number {
  const requireDisplay = opts?.requireDisplay !== false;
  const included = requireDisplay ? events.filter(showsPrize) : events;
  const results = useQueries({
    queries: included.map((e) => ({
      queryKey: prizeOpsKeys.eventPrize(e.id),
      queryFn: async () => {
        const res = await httpClient.get<{ data: EventPrizeComputation; success: boolean }>(
          `/prize-ops/events/${e.id}/prize`,
        );
        return res.data;
      },
      staleTime: 30 * 1000,
    })),
  });

  const totalById = new Map<string, number>();
  included.forEach((e, i) => {
    const computed = results[i]?.data?.calculation.total;
    totalById.set(e.id, computed ?? (Number(e.total_prize_amount) || 0));
  });

  return events.reduce((sum, e) => {
    if (requireDisplay && !showsPrize(e)) return sum; // toggle off → excluded
    return sum + (totalById.get(e.id) ?? 0);
  }, 0);
}

/**
 * Live computed prize total + effective adjustment breakdown for an event.
 * `entryCount` lets the setup form preview a hypothetical entry count; omit it
 * for the real (registration-derived) live number shown to contestants.
 */
export function useEventPrize(
  eventId: string | null | undefined,
  opts?: { entryCount?: number; enabled?: boolean },
) {
  return useQuery({
    queryKey: prizeOpsKeys.eventPrize(eventId || '', opts?.entryCount),
    enabled: !!eventId && opts?.enabled !== false,
    queryFn: async () => {
      const qs = opts?.entryCount !== undefined ? `?entry_count=${opts.entryCount}` : '';
      const res = await httpClient.get<{ data: EventPrizeComputation; success: boolean }>(
        `/prize-ops/events/${eventId}/prize${qs}`,
      );
      return res.data;
    },
    staleTime: 30 * 1000,
  });
}
